import { useFeatureIsOn } from "@growthbook/growthbook-react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Chip, Tooltip } from "@mui/material";
import {
  FieldsCard,
  FieldsCardBody,
  FieldsData,
  Link,
  Typography,
} from "@platform/design-system";
import {
  validateBetweenNumbers,
  validatePositiveNumber,
  validateValidDate,
} from "@platform/fe-utils";
import { Consultant } from "@platform/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useState } from "react";
import { Link as RouterLink } from "react-router";
import { useApi } from "@platform/api";
import { AxiosResponse } from "axios";
import { REMOVE_IGO } from "@platform/feature-flags";
import { useSignedInUser } from "@platform/user/client";

import { ROUTE_NAMES } from "../../../constants";
import {
  getRfpDetailsQueryKey,
  useRfpDetails,
} from "../../../hooks/useRfpDetails";
import useToastNotificationService from "../../../hooks/useToastNotificationService";
import { formatDate, getFullName, extractErrorMessage } from "../../../utils";
import { UpdateRfpDetailsRequest } from "../../../../../../src/libs/frontend/coverage-overview/src/lib/components/SendRfpDrawer/api/types";

import {
  AssignedAnalystField,
  buildAssignedAnalystField,
  parseAssignedAnalyst,
  SelectOption,
  validateNotBeforeToday,
  validateRevisedDueDate,
} from "./rfpDetailsCard.helpers";

import type { RfpDetailsResponse } from "../../../types/rfpApi";

function mapBodyToUpdateRequest(body: FieldsCardBody): UpdateRfpDetailsRequest {
  const isStrategic = body.strategicRfp === "yes";

  return {
    ...parseAssignedAnalyst(body.primaryAnalystId),
    secondaryAnalystId:
      typeof body.secondaryAnalystId === "number"
        ? body.secondaryAnalystId
        : undefined,
    revisedDueDate:
      typeof body.revisedDueDate === "string" ? body.revisedDueDate : undefined,
    revisedEffectiveDate:
      typeof body.revisedEffectiveDate === "string"
        ? body.revisedEffectiveDate
        : undefined,
    carrierDueDate:
      typeof body.carrierDueDate === "string" ? body.carrierDueDate : undefined,
    inGoodOrderDate:
      typeof body.inGoodOrderDate === "string"
        ? body.inGoodOrderDate
        : undefined,
    estimatedHours:
      typeof body.estimatedHours === "number" ? body.estimatedHours : undefined,
    totalPlansSpread:
      typeof body.totalPlansSpread === "number"
        ? body.totalPlansSpread
        : undefined,
    strategicRfp: isStrategic,
    strategicAccountAnalyst: isStrategic
      ? typeof body.strategicAccountAnalyst === "string"
        ? body.strategicAccountAnalyst
        : null
      : null,
    sfaTime: typeof body.sfaTime === "number" ? body.sfaTime : undefined,
  };
}

type Props = {
  rfpId: string;
  refetchRpfDetails: VoidFunction;
};

function RFPDetailsCard({
  rfpId,
  refetchRpfDetails,
}: Props): React.JSX.Element {
  const [isEdit, setIsEdit] = useState(false);
  const isRemoveIGOFlag = useFeatureIsOn(REMOVE_IGO);
  const { createToastNotification } = useToastNotificationService();
  const { isBrokerOrAgent } = useSignedInUser();
  const axiosInstance = useApi();

  const select = useCallback(
    (resp: RfpDetailsResponse): FieldsData[] =>
      mapResponseToFieldsCardData({
        resp,
        isBrokerOrAgent,
        isRemoveIGOFlag,
      }),
    [isBrokerOrAgent, isRemoveIGOFlag],
  );

  const {
    data: fieldsData,
    status: queryStatus,
    error,
  } = useRfpDetails<FieldsData[]>({ rfpId, enabled: !!rfpId, select });

  const queryClient = useQueryClient();
  const mutation = useMutation<void, Error, UpdateRfpDetailsRequest>({
    mutationFn: (data) =>
      axiosInstance
        .patch(`brokerportal/v1/rfp/${rfpId}/details`, data)
        .then((res: AxiosResponse<null, unknown>) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getRfpDetailsQueryKey(rfpId) });
      handleCancel();
      refetchRpfDetails();
    },
    onError: (error) => {
      createToastNotification({
        message: extractErrorMessage(error),
        severity: "error",
      });
    },
  });

  const handleCancel = (): void => {
    setIsEdit(false);
  };

  const status = mutation.isPending ? "saving" : isEdit ? "edit" : queryStatus;

  return (
    <FieldsCard
      title="RFP"
      status={status}
      error={error}
      gridContainerProps={{
        columns: 15,
      }}
      gridItemProps={{
        size: 3,
      }}
      skeleton={{
        count: 10,
        props: {
          height: 50,
        },
      }}
      onEdit={isBrokerOrAgent ? undefined : () => setIsEdit(true)}
      onCancel={isBrokerOrAgent ? undefined : handleCancel}
      onSave={
        isBrokerOrAgent
          ? undefined
          : (body) => {
              if (body) {
                mutation.mutate(mapBodyToUpdateRequest(body));
              } else {
                handleCancel();
              }
            }
      }
      data={fieldsData}
    />
  );
}

type ClientNameDisplayProps = {
  clientName: string;
  isProspect: boolean;
  clientId: string | undefined;
};

export const ClientNameDisplay: React.FC<ClientNameDisplayProps> = ({
  clientName,
  isProspect,
  clientId,
}) => {
  if (!clientName) return null;

  if (isProspect) {
    return (
      <>
        <Typography variant="display-small" component="span">
          {clientName}
        </Typography>{" "}
        <Chip label="Prospect" />
      </>
    );
  }

  if (clientId) {
    return (
      <Link
        component={RouterLink}
        to={`/${ROUTE_NAMES.CLIENTS}/${clientId}`}
        color="inherit"
        underline="hover"
        variant="h3"
      >
        {clientName}
      </Link>
    );
  }

  return (
    <Typography variant="display-small" component="span">
      {clientName}
    </Typography>
  );
};

function hasValidConsultantName(consultant: Consultant | null): boolean {
  return Boolean(consultant?.firstName && consultant?.lastName);
}

const inputSlotDateProps = {
  textField: {
    sx: {
      opacity: 0.54,
    },
    InputLabelProps: {
      sx: {
        paddingRight: "12px",
      },
    },
  },
};

function mapResponseToFieldsCardData({
  resp,
  isBrokerOrAgent,
  isRemoveIGOFlag,
}: {
  resp: RfpDetailsResponse;
  isBrokerOrAgent: boolean;
  isRemoveIGOFlag: boolean;
}): FieldsData[] {
  const analystOptions = resp.analystsList.map((a) => ({
    label: a.name,
    value: a.id.toString(),
  }));
  const primaryAnalystId = resp.primaryAnalyst?.id.toString() || "";
  const secondaryAnalystId = resp.secondaryAnalyst?.id.toString() || "";
  const assignedAnalyst = buildAssignedAnalystField(
    resp,
    analystOptions,
    secondaryAnalystId,
  );

  if (isBrokerOrAgent) {
    return getBrokerAgentFields(resp, assignedAnalyst);
  }

  return getAdminFields(
    resp,
    analystOptions,
    primaryAnalystId,
    secondaryAnalystId,
    assignedAnalyst,
    isRemoveIGOFlag,
  );
}

const getAdminFields = (
  resp: RfpDetailsResponse,
  analystOptions: SelectOption[],
  primaryAnalystId: string,
  secondaryAnalystId: string,
  assignedAnalyst: AssignedAnalystField,
  isRemoveIGOFlag: boolean,
): FieldsData[] => [
  {
    label: "Date Received",
    value: formatDate(resp.createdDate, true),
  },
  {
    label: "Original Due Date",
    value: formatDate(resp.originalDueDate, true),
  },
  {
    label: "Original Effective Date",
    value: formatDate(resp.originalEffectiveDate, true),
  },
  {
    label: "Points",
    value: resp.estimatedTotalHours ? resp.estimatedTotalHours.toFixed(2) : "",
    editable: false,
  } as FieldsData,
  {
    label: "Revised Points",
    value: resp.estimatedHours ? resp.estimatedHours.toString() : "",
    editable: true,
    inputType: "number" as const,
    inputName: "estimatedHours",
    validators: [validatePositiveNumber],
  } as FieldsData,
  {
    label: "Carrier Due Date",
    value: formatResponseDate(resp.carrierDueDate),
    editable: true,
    inputType: "date",
    inputName: "carrierDueDate",
    validators: [validateValidDate, validateNotBeforeToday],
    inputProps: {
      disablePast: true,
      formatResultValue: formatDateToStartOfDay,
      slotProps: inputSlotDateProps,
    },
  },
  ...(isRemoveIGOFlag
    ? []
    : [
        {
          label: "In Good Order Date",
          value: formatResponseDate(resp.inGoodOrderDate),
          editable: true,
          inputType: "date",
          inputName: "inGoodOrderDate",
          validators: [validateValidDate],
          inputProps: {
            formatResultValue: formatDateToStartOfDay,
            slotProps: inputSlotDateProps,
          },
        } as FieldsData,
      ]),
  {
    label: "Assigned Analyst",
    value: assignedAnalyst.displayName,
    inputValue: assignedAnalyst.inputValue,
    editable: true,
    inputType: "select",
    inputName: "primaryAnalystId",
    inputProps: {
      options: assignedAnalyst.options,
    },
  },
  {
    label: "Secondary Analyst",
    value: resp.secondaryAnalyst?.name,
    inputValue: secondaryAnalystId,
    editable: true,
    inputType: "select",
    inputName: "secondaryAnalystId",
    inputProps: {
      options: analystOptions.filter((a) => a.value !== primaryAnalystId),
    },
  },
  {
    label: "Consultant",
    value: hasValidConsultantName(resp.consultant)
      ? (getFullName(resp.consultant) as string)
      : null,
  },
  {
    label: "Total Plans Spread",
    value: resp.totalPlansSpread ? resp.totalPlansSpread.toString() : "",
    editable: true,
    inputType: "number",
    inputName: "totalPlansSpread",
    validators: [validateBetweenNumbers(0, 999)],
  },
  {
    label: "Strategic RFP",
    value: resp.strategicRfp ? "Yes" : "No",
    inputValue: resp.strategicRfp ? "yes" : "no",
    editable: true,
    inputName: "strategicRfp",
    inputType: "select",
    inputProps: {
      helperText: "Is this RFP highly complex or high revenue producing?",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
    },
  },
  {
    label: {
      name: "Strategic Account Analyst",
      icon: (
        <Tooltip title="If this is a strategic RFP, this is where the Strategic Account Analyst's name should be entered">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      ),
    },
    value: resp.strategicAccountAnalyst,
    editable: true,
    inputType: "text",
    inputName: "strategicAccountAnalyst",
    visibleViewMode: resp.strategicRfp && !!resp.strategicAccountAnalyst,
    visibleWhen: (values) => values.strategicRfp === "yes",
  },
  {
    label: {
      name: "SFA Time",
      icon: (
        <Tooltip title="Estimated project hours of Senior Financial Analyst">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      ),
    },
    value: resp.sfaTime ? resp.sfaTime.toString() : "",
    editable: true,
    inputType: "number",
    inputName: "sfaTime",
    validators: [validateBetweenNumbers(1, 99)],
    visibleViewMode: !!resp.sfaTime,
    inputProps: {
      helperText: "Estimated project hours of Senior Financial Analyst",
    },
  },
  {
    label: "Revised Due Date",
    value: formatResponseDate(resp.revisedDueDate),
    editable: true,
    inputType: "date",
    inputName: "revisedDueDate",
    validators: [
      validateValidDate,
      validateRevisedDueDate(formatResponseDate(resp.createdDate)),
    ],
    visibleViewMode: !!resp.revisedDueDate,
    inputProps: {
      minDate: formatResponseDate(resp.createdDate) ?? undefined,
      formatResultValue: formatDateToStartOfDay,
      slotProps: inputSlotDateProps,
    },
  },
  {
    label: "Revised Effective Date",
    value: formatResponseDate(resp.revisedEffectiveDate),
    editable: true,
    inputType: "date",
    inputName: "revisedEffectiveDate",
    validators: [validateValidDate],
    visibleViewMode: !!resp.revisedEffectiveDate,
    inputProps: {
      formatResultValue: formatDateToStartOfDay,
      slotProps: inputSlotDateProps,
    },
  },
  {
    label: "Marketing Objective",
    value: resp.additionalInfo?.marketingObjectives ?? "",
    gridItemProps: {
      size: 15,
    },
  },
];
const getBrokerAgentFields = (
  resp: RfpDetailsResponse,
  assignedAnalyst: AssignedAnalystField,
): FieldsData[] => [
  {
    label: "Original Due Date",
    value: formatDate(resp.originalDueDate, true),
  },
  {
    label: "Revised Due Date",
    value: formatResponseDate(resp.revisedDueDate),
    editable: true,
    inputType: "date",
    inputName: "revisedDueDate",
    validators: [
      validateValidDate,
      validateRevisedDueDate(formatResponseDate(resp.createdDate)),
    ],
    visibleViewMode: !!resp.revisedDueDate,
    inputProps: {
      minDate: formatResponseDate(resp.createdDate) ?? undefined,
      formatResultValue: formatDateToStartOfDay,
      slotProps: inputSlotDateProps,
    },
  },
  {
    label: "Original Effective Date",
    value: formatDate(resp.originalEffectiveDate, true),
  },
  {
    label: "Revised Effective Date",
    value: formatResponseDate(resp.revisedEffectiveDate),
    editable: true,
    inputType: "date",
    inputName: "revisedEffectiveDate",
    validators: [validateValidDate],
    visibleViewMode: !!resp.revisedEffectiveDate,
    inputProps: {
      formatResultValue: formatDateToStartOfDay,
      slotProps: inputSlotDateProps,
    },
  },
  {
    label: "Assigned Analyst",
    value: assignedAnalyst.displayName,
  },
  {
    label: {
      name: "Assigned Analyst Email",
      allowCopy: true,
    },
    value: assignedAnalyst.email,
  },
  {
    label: "Consultant",
    value: hasValidConsultantName(resp.consultant)
      ? (getFullName(resp.consultant) as string)
      : null,
  },
  {
    label: "Marketing Objective",
    value: resp.additionalInfo?.marketingObjectives ?? "",
    gridItemProps: {
      size: 15,
    },
  },
];

function formatResponseDate(date: string | null): Date | null {
  if (!date) {
    return null;
  }

  const dateString = date.split("T")[0];
  const dateParts = dateString.split("-");
  const year = +dateParts[0];
  // Months are zero-based in JavaScript
  const month = +dateParts[1] - 1;
  const day = +dateParts[2];
  return new Date(year, month, day);
}

function formatDateToStartOfDay(date: Date | null): string | null {
  if (!date) {
    return null;
  }

  // Create new Date with just the year, month, and day to set time to midnight
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Create new date with time at midnight UTC
  const dateWithZeroTime = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));

  return dateWithZeroTime.toISOString();
}

export default RFPDetailsCard;
